import React, { useState, useEffect, useRef } from 'react';
import CareerDetailsAbout from './CareerDetailsAbout';
import CareerTopCollege from './CareerTopCollege';
import CareerProspects from './CareerProspects';
import CareerProsCons from './CareerProsCons';
import CareerFamousPersonality from './CareerFamousPersonality';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faLock } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

// Type definitions
interface CareerData {
  name: string;
  about_section?: string;
  large_image?: string;
  thumbnail_image?: string;
}

interface TabStatuses {
  about?: boolean;
  topColleges: boolean;
  careerProspects: boolean;
  prosCons: boolean;
  famousPersonalities: boolean;
}

interface BasicInfoApiResponse {
  success: boolean;
  data: CareerData[];
}

interface TabsApiResponse {
  success: boolean;
  data: {
    top_college_status: number;
    prospects_status: number;
    proscons_status: number;
    personalities_status: number;
  };
}

type TabName = 'about' | 'topColleges' | 'careerProspects' | 'prosCons' | 'famousPersonalities';

const CareerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [careerData, setCareerData] = useState<CareerData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabName | undefined>();
  const [tabStatuses, setTabStatuses] = useState<TabStatuses>({
    topColleges: false,
    careerProspects: false,
    prosCons: false,
    famousPersonalities: false,
  });

  const aboutRef = useRef<HTMLDivElement>(null);
  const topCollegesRef = useRef<HTMLDivElement>(null);
  const careerProspectsRef = useRef<HTMLDivElement>(null);
  const prosConsRef = useRef<HTMLDivElement>(null);
  const famousPersonalitiesRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // const getSetProductMenu = async () => {
    //   const userData = JSON.parse(localStorage.getItem('user') || '{}');
    //   if (!userData) return;
    //   const school_id = userData.instituteId;
    //   const class_code = userData.pursuingEducationLevelCode;

    //   try {
    //       const response = await axios.post(`${process.env.REACT_APP_COMMON_API}/test/get-test-menu-by-schoolid_campuscode`, {
    //           SchoolId: school_id,
    //           ClassCode: class_code,
    //       });
    //       if (response.data.success) {
    //           sessionStorage.setItem('product-menu-test', JSON.stringify(response.data.data));
    //       } else {
    //           console.error('Error:', response.data.message);
    //       }
    //   } catch (error) {
    //       console.error('Error fetching product menu data:', error);
    //   }
    // }

    // // const StudenttestMenu = JSON.parse(sessionStorage.getItem('product-menu-test') || '{}');
    // const StudenttestMenu = JSON.parse(localStorage.getItem('product-menu-test') || '{}');
    // if (!StudenttestMenu || Object.keys(StudenttestMenu).length === 0) {
    //   getSetProductMenu();
    // }
    if (id) {
      fetchCareerData(id);
      fetchCareerTabs(id);
    }
  }, [id]);

  const fetchCareerData = async (career_id: string): Promise<void> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SFE_API}career/fetchBasicInfo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ career_id }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result: BasicInfoApiResponse = await response.json();

      if (result.success) {
        setCareerData(result.data[0]);
      } else {
        setError('Failed to fetch career data');
      }
    } catch (error) {
      setError('An error occurred while fetching the data.');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCareerTabs = async (career_id: string): Promise<void> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SFE_API}career/showCareerTabs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ career_id }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result: TabsApiResponse = await response.json();
      if (result.success) {
        const { top_college_status, prospects_status, proscons_status, personalities_status } = result.data;
        setTabStatuses({
          about: true,
          topColleges: top_college_status === 1,
          careerProspects: prospects_status === 1,
          prosCons: proscons_status === 1,
          famousPersonalities: personalities_status === 1,
        });
      } else {
        setError('Failed to fetch tab statuses');
      }
    } catch (error) {
      setError('An error occurred while fetching tab statuses.');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleScroll = (): void => {
      if (!aboutRef.current || !topCollegesRef.current || !prosConsRef.current || !famousPersonalitiesRef.current || !careerProspectsRef.current) return;

      const aboutOffset = aboutRef.current.getBoundingClientRect().top;
      const topCollegesOffset = topCollegesRef.current.getBoundingClientRect().top;
      const careerProspectsOffset = careerProspectsRef.current.getBoundingClientRect().top;
      const prosConsOffset = prosConsRef.current.getBoundingClientRect().top;
      const famousPersonalitiesOffset = famousPersonalitiesRef.current.getBoundingClientRect().top;

      if (aboutOffset >= 0 && aboutOffset < window.innerHeight) {
        setActiveTab('about');
      } else if (topCollegesOffset >= 0 && topCollegesOffset < window.innerHeight) {
        setActiveTab('topColleges');
      } else if (careerProspectsOffset >= 0 && careerProspectsOffset < window.innerHeight) {
        setActiveTab('careerProspects');
      } else if (prosConsOffset >= 0 && prosConsOffset < window.innerHeight) {
        setActiveTab('prosCons');
      } else if (famousPersonalitiesOffset >= 0 && famousPersonalitiesOffset < window.innerHeight) {
        setActiveTab('famousPersonalities');
      }
    };

    // Set active tab to 'about' on load
    const timeoutId = setTimeout(() => {
      setActiveTab('about');
    }, 500);

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);

    // Cleanup listener on component unmount
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleTabChange = (tabName: TabName, ref: React.RefObject<HTMLDivElement>): void => {
    setActiveTab(tabName);
    if (ref && ref.current) {
      const yOffset = -100;
      const y = ref.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };
  
  useEffect(() => {
    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (entry.target === aboutRef.current) {
            setActiveTab('about');
          } else if (entry.target === topCollegesRef.current) {
            setActiveTab('topColleges');
          } else if (entry.target === careerProspectsRef.current) {
            setActiveTab('careerProspects');
          } else if (entry.target === prosConsRef.current) {
            setActiveTab('prosCons');
          } else if (entry.target === famousPersonalitiesRef.current) {
            setActiveTab('famousPersonalities');
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, options);

    if (aboutRef.current) observer.observe(aboutRef.current);
    if (topCollegesRef.current && tabStatuses.topColleges) observer.observe(topCollegesRef.current);
    if (careerProspectsRef.current && tabStatuses.careerProspects) observer.observe(careerProspectsRef.current);
    if (prosConsRef.current && tabStatuses.prosCons) observer.observe(prosConsRef.current);
    if (famousPersonalitiesRef.current && tabStatuses.famousPersonalities) observer.observe(famousPersonalitiesRef.current);

    return () => {
      observer.disconnect();
    };
  }, [tabStatuses]);

  if (loading) return <div>Loading...</div>;

  // const StudenttestMenu = JSON.parse(sessionStorage.getItem('product-menu-test') || '{}');
  const StudenttestMenu = JSON.parse(localStorage.getItem('product-menu-test') || '{}');
  const allowAccess = StudenttestMenu && Object.keys(StudenttestMenu).length > 0 
          && StudenttestMenu.career_deep_dive === true;

  if (!allowAccess && !loading) {
      return (
          <main className='flex-1'>
              <div className="mx-auto max-420 my-5 text-center">
                  <div className="access-denied-card">
                      <FontAwesomeIcon icon={faLock} className="access-denied-icon font-48 text-danger mb-3" />
                      <h2 className='font-26 font-500 mb-1'>Access Denied</h2>
                      <p className='font-18 mb-2'>You do not have permission to access this page.</p>
                      <p className='font-18 mb-4'>Please contact support for more information.</p>
                      <button className="btn-border-gray" onClick={() => navigate(-1)}>
                          <span>
                              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                              Go Back
                          </span>
                      </button>
                  </div>
              </div>
          </main>
      );
  }

  return (
    <>
      <div className="main sideBarEnable flex-1">
        <div className="flex-1 d-flex flex-column">
          <div className="flex-1">
            <div className="max-1140 mx-auto">
              <div className="row g-2 pt-5 pb-3">
                <div className="col">
                  <h3>{careerData ? careerData.name : 'Loading...'}</h3>
                </div>
              </div>
            </div>

            <div className="max-1140 mx-auto position-sticky top-0 z1">
              <div className="xscroll job_internship_tab polartabsec">
                <ul className="nav nav-tabs">
                  {tabStatuses.about && (
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'about' ? 'active' : ''}`}
                        onClick={() => handleTabChange('about', aboutRef)}
                      >
                        About
                      </a>
                    </li>
                  )}
                  {tabStatuses.topColleges && (
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'topColleges' ? 'active' : ''}`}
                        onClick={() => handleTabChange('topColleges', topCollegesRef)}
                      >
                        Top Colleges
                      </a>
                    </li>
                  )}
                  {tabStatuses.careerProspects && (
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'careerProspects' ? 'active' : ''}`}
                        onClick={() => handleTabChange('careerProspects', careerProspectsRef)}
                      >
                        Career Prospects
                      </a>
                    </li>
                  )}
                  {tabStatuses.prosCons && (
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'prosCons' ? 'active' : ''}`}
                        onClick={() => handleTabChange('prosCons', prosConsRef)}
                      >
                        Pros and Cons
                      </a>
                    </li>
                  )}
                  {tabStatuses.famousPersonalities && (
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'famousPersonalities' ? 'active' : ''}`}
                        onClick={() => handleTabChange('famousPersonalities', famousPersonalitiesRef)}
                      >
                        Famous Personalities
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            <div ref={aboutRef}>
              <CareerDetailsAbout />
            </div>
            <div ref={topCollegesRef}>
              <CareerTopCollege />
            </div>
            <div ref={careerProspectsRef}>
              <CareerProspects />
            </div>
            <div ref={prosConsRef}>
              <CareerProsCons />
            </div>
            <div ref={famousPersonalitiesRef}>
              <CareerFamousPersonality />
            </div>
          </div>

          <div className="footer max-1140 mx-auto">
            <div className="row g-2"></div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CareerDetails;